import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Printer } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";

interface Section {
  id: number;
  heading: string;
  body: string;
}

let idCounter = 300;
const nextId = () => idCounter++;

const TOOL_INFO_CONTENT = {
  what: `Builds a structured, properly formatted user manual from your own content, organized into standard sections like Overview, Getting Started, and Troubleshooting.`,
  how: `Fill in each section with your own content, add or remove sections as needed, and export the finished manual as a print-ready PDF.`,
  tip: `Write the Troubleshooting section last, after you've documented the normal workflow — it's much easier to anticipate what will actually confuse someone once you've walked through the full process yourself.`,
};

export default function UserManualWriter() {
  const [productName, setProductName] = useState("Product Name");
  const [version, setVersion] = useState("v1.0");
  const [sections, setSections] = useState<Section[]>([
    {
      id: nextId(),
      heading: "Overview",
      body: "A brief description of what this product does and who it's for.",
    },
    {
      id: nextId(),
      heading: "Getting Started",
      body: "Step-by-step setup instructions.",
    },
    {
      id: nextId(),
      heading: "How to Use",
      body: "Walk through the core features.",
    },
    {
      id: nextId(),
      heading: "Troubleshooting",
      body: "Common issues and how to resolve them.",
    },
    {
      id: nextId(),
      heading: "Safety Warnings",
      body: "Any important safety information.",
    },
    { id: nextId(), heading: "Support", body: "How to contact support." },
  ]);

  const addSection = () =>
    setSections([
      ...sections,
      { id: nextId(), heading: "New Section", body: "" },
    ]);
  const removeSection = (id: number) =>
    setSections(sections.filter(s => s.id !== id));
  const updateSection = (id: number, field: keyof Section, value: string) =>
    setSections(
      sections.map(s => (s.id === id ? { ...s, [field]: value } : s))
    );

  const { logUsageNow } = useLogUsageOnAction();


  return (
    <ToolContainer>
      <style>{`
        @media print {
          /* display:none (not visibility:hidden) so the hidden page does not add blank pages */
          body *:not(#manual-print-area):not(#manual-print-area *):not(:has(#manual-print-area)) { display: none !important; }
          html, body { height: auto !important; overflow: visible !important; }
          #manual-print-area { position: static; width: 100%; }
          @page { margin: 0.75in; }
        }
      `}</style>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4 print:hidden">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block text-sm">Product Name</Label>
              <Input aria-label="Product Name"
                value={productName}
                onChange={e => setProductName(e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1 block text-sm">Version</Label>
              <Input aria-label="Version"
                value={version}
                onChange={e => setVersion(e.target.value)}
              />
            </div>
          </div>

          {sections.map(section => (
            <div key={section.id} className="rounded-lg border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Input
                  value={section.heading}
                  onChange={e =>
                    updateSection(section.id, "heading", e.target.value)
                  }
                  className="font-semibold"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => removeSection(section.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <Textarea
                value={section.body}
                onChange={e =>
                  updateSection(section.id, "body", e.target.value)
                }
                className="min-h-[100px] text-sm"
                placeholder="Content for this section..."
              />
            </div>
          ))}

          <div className="flex gap-3">
            <Button variant="outline" onClick={addSection}>
              <Plus className="h-4 w-4 mr-2" /> Add Section
            </Button>
            <Button onClick={async () => { const allowed = await logUsageNow(); if (!allowed) return; window.print(); }}>
              <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
            </Button>
          </div>
        </div>

        <div
          id="manual-print-area"
          className="rounded-lg border p-10 bg-card print:border-0"
        >
          <div className="text-center mb-8 pb-6 border-b">
            <div className="text-2xl font-bold">{productName}</div>
            <p className="text-sm text-muted-foreground mt-1">
              User Manual — {version}
            </p>
          </div>
          {sections.length > 2 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-2">Contents</h2>
              <ol className="text-sm space-y-0.5 text-muted-foreground list-none">
                {sections.map((section, i) => (
                  <li key={section.id}>{i + 1}. {section.heading}</li>
                ))}
              </ol>
            </div>
          )}
          {sections.map((section, i) => (
            <div key={section.id} className="mb-6 break-inside-avoid">
              <h2 className="text-lg font-semibold mb-2">
                {i + 1}. {section.heading}
              </h2>
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
                {section.body}
              </p>
            </div>
          ))}
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
